import { useMemo } from 'react';

import { is3pApp } from '@atlassian/conversation-assistant-chat-prompt-input/utils/is-3p-app';
import type { AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import {
	useConnectorsDataReady,
	useEnabledKnowledgeSources,
	useKnowledgeSourcesStore,
} from '@atlassian/conversation-assistant-store/controllers/connectors-store';
import { useEnsureConnectorsLoaded } from '@atlassian/conversation-assistant-store/controllers/connectors-store/use-ensure-connectors-loaded';

export type ThirdPartySuggestedPromptEligibility = {
	/** True once the connectors config has loaded (regardless of whether the user is eligible). */
	connectorsLoaded: boolean;
	/**
	 * True when the admin has allowed at least one 3P knowledge source. Ineligible users behave
	 * like control and fire no exposure, so they don't dilute the experiment.
	 */
	isEligible: boolean;
	/** Count of admin-allowed knowledge sources that are 3P apps. */
	adminAllowed3pAppCount: number;
	/** Count of all admin-allowed knowledge sources (1P + 3P). */
	knowledgeSourceCount: number;
	/** True when the connectors fetch errored, so `isEligible: false` means "couldn't determine". */
	connectorsErrored: boolean;
};

const NOT_ELIGIBLE_DEFAULT = {
	isEligible: false,
	adminAllowed3pAppCount: 0,
	knowledgeSourceCount: 0,
	connectorsErrored: false,
} as const;

/**
 * Resolves 3P-suggested-prompt-tile eligibility from connector data: is the admin
 * allowing at least one 3P app. Deliberately does NOT read the experiment — the
 * cohort read and exposure live in `useThirdPartySuggestedPromptTile`.
 *
 * `enabled` gates the connectors fetch: pass `false` on surfaces that don't need
 * eligibility (control on a preload surface) so the create button doesn't pay a
 * connectors fetch — and the re-render it triggers — for every user.
 */
export const useThirdPartySuggestedPromptEligibility = ({
	assistanceServiceParams,
	enabled = true,
}: AssistanceServiceInputParam & { enabled?: boolean }): ThirdPartySuggestedPromptEligibility => {
	useEnsureConnectorsLoaded({
		assistanceServiceParams: { ...assistanceServiceParams, experienceId: 'ai-mate' },
		enabled,
	});
	const [connectorsLoaded] = useConnectorsDataReady();
	const [knowledgeSources] = useEnabledKnowledgeSources();
	const [{ error: connectorsError }] = useKnowledgeSourcesStore();

	return useMemo(() => {
		if (!connectorsLoaded) {
			return { connectorsLoaded: false, ...NOT_ELIGIBLE_DEFAULT };
		}
		const adminAllowed3pAppCount = knowledgeSources.filter((connector) =>
			is3pApp(connector.friendlyName),
		).length;
		return {
			connectorsLoaded: true,
			isEligible: adminAllowed3pAppCount > 0,
			adminAllowed3pAppCount,
			knowledgeSourceCount: knowledgeSources.length,
			connectorsErrored: Boolean(connectorsError),
		};
	}, [connectorsLoaded, knowledgeSources, connectorsError]);
};
